import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../context/AppStore';
import { Button, Card, Badge, Spinner, Input, NetworkIcon, Toggle } from '../components/ui';
import { NETWORK_CONFIG } from '../api/mockData';
import { hashtagsAPI, captionTemplatesAPI, bulkPublishAPI, billingAPI, providersAPI } from '../api/client';
import MediaLibraryPage from './MediaLibrary';
import { postsAPI, projectsAPI } from '../api/client';

const STEPS = ['Media', 'Análisis IA', 'Contenido', 'Redes & Destino', 'Publicar'];

const TONES = [
  { id: 'profesional', label: '👔 Profesional' },
  { id: 'casual', label: '😎 Casual' },
  { id: 'inspiracional', label: '✨ Inspiracional' },
  { id: 'humoristico', label: '😄 Humorístico' },
];

function StepIndicator({ current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: '32px', flexWrap: 'wrap' }}>
      {STEPS.map((s, i) => (
        <div key={s} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
            <div style={{
              width: i === current ? '36px' : '30px',
              height: i === current ? '36px' : '30px',
              borderRadius: '50%',
              background: i < current ? '#7c3aed' : i === current ? 'var(--gradient-primary)' : 'var(--bg-elevated)',
              border: i === current ? '2px solid #c084fc' : i < current ? '2px solid #7c3aed' : '2px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '12px', fontWeight: '800',
              color: i <= current ? '#fff' : 'var(--text-muted)',
              transition: 'all 0.3s',
              boxShadow: i === current ? '0 0 20px #a855f750' : 'none',
            }}>
              {i < current ? '✓' : i + 1}
            </div>
            <span style={{ fontSize: '10px', fontWeight: i === current ? '700' : '400', color: i === current ? '#c084fc' : i < current ? '#7c3aed' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>{s}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{ width: '32px', height: '2px', background: i < current ? '#7c3aed' : 'var(--border)', marginBottom: '22px', transition: 'all 0.4s' }} />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Step 0: Upload ──────────────────────────────────────
// ── Constantes de costo estimado ─────────────────────────
const PROVIDER_SUPPORT = {
  deepseek: { image: false, video: false, label: 'DeepSeek', color: '#4f8ef7', icon: '🧠' },
  openai:   { image: true,  video: true,  label: 'GPT-4o',   color: '#10a37f', icon: '🤖' },
  gemini:   { image: true,  video: true,  label: 'Gemini',   color: '#4285f4', icon: '♊' },
  claude:   { image: true,  video: false, label: 'Claude',   color: '#cc785c', icon: '⚡' },
  groq:     { image: true,  video: false, label: 'Groq',     color: '#f55036', icon: '🔥' },
  grok:     { image: false, video: false, label: 'Grok',     color: '#e7e7e7', icon: '𝕏' },
};

const COST_ESTIMATE = {
  image: { tokens_in: 800,  tokens_out: 400, label: '~1,200 tokens' },
  video: { tokens_in: 2000, tokens_out: 600, label: '~2,600 tokens' },
};

function AIReadinessPanel({ file, subscription, activeProvider, onAnalyze, onManual, analyzing }) {
  if (!file) return null;

  const isVideo   = file.type?.startsWith('video');
  const mediaType = isVideo ? 'video' : 'image';
  const estimate  = COST_ESTIMATE[mediaType];
  const support   = PROVIDER_SUPPORT[activeProvider?.slug] || {};
  const canAnalyze = isVideo ? support.video : support.image;

  // Calcular costo estimado
  const costPerTokenIn  = activeProvider?.cost_per_1k ? activeProvider.cost_per_1k / 1000 : 0;
  const estimatedCost   = (estimate.tokens_in + estimate.tokens_out) * costPerTokenIn;
  const creditRemaining = subscription ? (subscription.ai_credit_limit + subscription.ai_credit_rollover - subscription.ai_credit_used) : 0;
  const canAfford       = estimatedCost === 0 || creditRemaining >= estimatedCost;
  const tokenLimit = subscription?.plan?.max_tokens_monthly ?? -1;
  const tokensUsed = subscription?.tokens_used_monthly ?? 0;
  const tokensRemaining = tokenLimit === -1 ? Infinity : Math.max(0, tokenLimit - tokensUsed);
  const estimatedTokens = (estimate.tokens_in || 0) + (estimate.tokens_out || 0);
  const tokenExhausted = tokenLimit !== -1 && tokensUsed >= tokenLimit;
  const tokenInsufficient = tokenLimit !== -1 && !tokenExhausted && estimatedTokens > 0 && tokensRemaining < estimatedTokens;
  const tokenPercent = tokenLimit > 0 ? Math.min(100, (tokensUsed / tokenLimit) * 100) : 0;
  const creditExhausted = tokenExhausted || tokenInsufficient || (subscription && subscription.ai_credit_limit > 0 && creditRemaining <= 0);
  const creditPercent   = subscription ? subscription.credit_percent : 0;
  const creditColor     = creditPercent >= 95 ? '#ef4444' : creditPercent >= 85 ? '#f97316' : creditPercent >= 70 ? '#f59e0b' : '#10b981';

  // Proveedor sugerido si el activo no soporta
  const suggested = !canAnalyze ? (isVideo ? 'Gemini 2.0 Flash' : 'Groq o Gemini') : null;

  return (
    <div className="animate-slideInUp" style={{
      background: 'var(--bg-elevated)',
      border: `1px solid ${canAnalyze && canAfford ? 'var(--border)' : '#f59e0b40'}`,
      borderRadius: 'var(--radius-xl)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 18px',
        background: canAnalyze && canAfford ? 'var(--bg-card)' : '#f59e0b08',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: '10px',
      }}>
        <span style={{ fontSize: '22px' }}>{isVideo ? '🎬' : '🖼'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '700', fontSize: '14px' }}>
            {isVideo ? 'Video detectado' : 'Imagen detectada'}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
          </div>
        </div>
        {activeProvider && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '6px 12px', borderRadius: '99px',
            background: `${support.color || '#888'}20`,
            border: `1px solid ${support.color || '#888'}40`,
          }}>
            <span style={{ fontSize: '14px' }}>{support.icon || '🤖'}</span>
            <span style={{ fontSize: '12px', fontWeight: '700', color: support.color || 'var(--text-primary)' }}>
              {support.label || activeProvider.name}
            </span>
            <span style={{ fontSize: '11px', color: canAnalyze ? '#10b981' : '#ef4444' }}>
              {canAnalyze ? '✓' : '✗'}
            </span>
          </div>
        )}
      </div>

      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

        {/* Caso 1: Proveedor no soporta el tipo */}
        {!canAnalyze && (
          <div style={{
            padding: '12px 14px',
            background: '#f59e0b10',
            border: '1px solid #f59e0b40',
            borderRadius: 'var(--radius-md)',
          }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '18px', flexShrink: 0 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: '700', fontSize: '13px', color: '#f59e0b', marginBottom: '4px' }}>
                  {activeProvider?.name || 'Tu proveedor actual'} no analiza {isVideo ? 'videos' : 'imágenes'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  Para generar captions basados en el contenido visual de tu {isVideo ? 'video' : 'imagen'},
                  activa <strong>{suggested}</strong> en{' '}
                  <span
                    onClick={() => window.location.href = '/providers'}
                    style={{ color: 'var(--accent-primary)', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Proveedores de IA →
                  </span>
                  {' '}o escribe el caption manualmente.
                </div>
                {isVideo && (
                  <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span>✅ <strong>Gemini 2.0 Flash</strong> — analiza video · ~$0.001/min</span>
                    <span>✅ <strong>GPT-4o</strong> — analiza frames · ~$0.005/análisis</span>
                    <span>❌ Groq, DeepSeek, Grok — no soportan video</span>
                  </div>
                )}
                {!isVideo && (
                  <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span>✅ <strong>Groq</strong> — gratuito · Llama Vision</span>
                    <span>✅ <strong>Gemini 2.0 Flash</strong> — muy económico</span>
                    <span>✅ <strong>GPT-4o / Claude</strong> — alta calidad</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Caso 2: Soporta pero sin crédito */}
        {canAnalyze && !canAfford && estimatedCost > 0 && (
          <div style={{
            padding: '12px 14px',
            background: '#ef444410',
            border: '1px solid #ef444430',
            borderRadius: 'var(--radius-md)',
          }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '18px', flexShrink: 0 }}>🚫</span>
              <div>
                <div style={{ fontWeight: '700', fontSize: '13px', color: '#ef4444', marginBottom: '4px' }}>
                  Crédito IA insuficiente
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  Este análisis requiere <strong>${estimatedCost.toFixed(6)}</strong> y tienes{' '}
                  <strong style={{ color: '#ef4444' }}>${creditRemaining.toFixed(6)}</strong> disponible.
                  Actualiza tu plan o escribe el caption manualmente.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Estimado de costo — solo si puede analizar */}
        {canAnalyze && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
            {[
              { icon: '⚡', label: 'Tokens estimados', val: estimate.label },
              {
                icon: '💰', label: 'Costo estimado',
                val: estimatedCost === 0 ? '🎉 Gratis' : `~$${estimatedCost.toFixed(6)}`,
                color: estimatedCost === 0 ? '#10b981' : undefined,
              },
              {
                icon: '💳', label: 'Crédito disponible',
                val: `$${creditRemaining.toFixed(4)}`,
                color: canAfford ? '#10b981' : '#ef4444',
              },
            ].map(s => (
              <div key={s.label} style={{
                padding: '10px 12px',
                background: 'var(--bg-card)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
              }}>
                <div style={{ fontSize: '16px', marginBottom: '4px' }}>{s.icon}</div>
                <div style={{ fontWeight: '800', fontSize: '14px', color: s.color || 'var(--text-primary)' }}>{s.val}</div>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Barra de tokens */}
        {tokenLimit !== -1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-muted)' }}>
              <span>Tokens IA del período</span>
              <span style={{ fontWeight: '700', color: tokenPercent >= 100 ? '#ef4444' : tokenPercent >= 85 ? '#f97316' : tokenPercent >= 70 ? '#f59e0b' : '#10b981' }}>
                {tokensUsed.toLocaleString()} / {tokenLimit.toLocaleString()}
              </span>
            </div>
            <div style={{ height: '6px', background: 'var(--bg-hover)', borderRadius: '99px', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${Math.min(tokenPercent, 100)}%`,
                background: tokenPercent >= 100 ? '#ef4444' : tokenPercent >= 85 ? '#f97316' : tokenPercent >= 70 ? '#f59e0b' : '#10b981',
                borderRadius: '99px', transition: 'width 0.5s',
              }} />
            </div>
            {tokenInsufficient && (
              <div style={{ fontSize: '10px', color: '#f59e0b' }}>
                ⚠️ Te quedan <strong>{tokensRemaining.toLocaleString()}</strong> tokens pero el análisis requiere ~<strong>{estimatedTokens.toLocaleString()}</strong>
              </div>
            )}
            {!tokenInsufficient && !tokenExhausted && estimatedTokens > 0 && tokensRemaining !== Infinity && (
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                Después del análisis quedarán ~<strong>{(tokensRemaining - estimatedTokens).toLocaleString()}</strong> tokens
              </div>
            )}
          </div>
        )}
        {/* Barra de crédito USD (solo si no hay límite de tokens) */}
        {tokenLimit === -1 && subscription && subscription.ai_credit_limit > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-muted)' }}>
              <span>Crédito IA del período</span>
              <span style={{ fontWeight: '700', color: creditColor }}>{creditPercent}% usado</span>
            </div>
            <div style={{ height: '6px', background: 'var(--bg-hover)', borderRadius: '99px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.min(creditPercent, 100)}%`, background: creditColor, borderRadius: '99px', transition: 'width 0.5s' }} />
            </div>
          </div>
        )}

        {/* Botones de decisión */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {canAnalyze && creditExhausted && (
            <div style={{ width: '100%', padding: '14px 16px', borderRadius: 'var(--radius-lg)', background: '#ef444410', border: '1px solid #ef444440' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <span style={{ fontSize: '20px' }}>🔒</span>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '13px', color: '#ef4444' }}>
                    {tokenExhausted ? 'Límite mensual de tokens alcanzado' : tokenInsufficient ? 'Tokens insuficientes para el análisis' : 'Crédito IA agotado'}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    {tokenExhausted
                      ? `Consumiste ${tokensUsed.toLocaleString()} de ${tokenLimit.toLocaleString()} tokens este mes.`
                      : tokenInsufficient
                        ? `Te quedan ${tokensRemaining.toLocaleString()} tokens pero el análisis requiere ~${estimatedTokens.toLocaleString()}.`
                        : 'Has consumido todo tu crédito IA del período.'}
                    {' '}Escribe el caption manualmente o actualiza tu plan.
                  </div>
                </div>
              </div>
              {tokenExhausted && (
                <div style={{ background: 'var(--bg-elevated)', borderRadius: '6px', overflow: 'hidden', height: '6px' }}>
                  <div style={{ width: '100%', height: '100%', background: '#ef4444', borderRadius: '6px' }} />
                </div>
              )}
            </div>
          )}
          {canAnalyze && !creditExhausted && canAfford && (
            <button
              onClick={onAnalyze}
              disabled={analyzing}
              style={{
                flex: 2, minWidth: '160px', padding: '12px 20px',
                borderRadius: 'var(--radius-lg)', border: 'none', cursor: 'pointer',
                background: 'var(--gradient-primary)', color: '#fff',
                fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: '700',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                opacity: analyzing ? 0.7 : 1,
              }}
            >
              {analyzing ? (
                <>
                  <span className="animate-spin" style={{ display: 'inline-block' }}>⟳</span>
                  Analizando...
                </>
              ) : (
                <>
                  ✨ Analizar con IA
                  {estimatedCost === 0
                    ? <span style={{ fontSize: '11px', opacity: 0.8 }}>(gratis)</span>
                    : <span style={{ fontSize: '11px', opacity: 0.8 }}>~${estimatedCost.toFixed(4)}</span>
                  }
                </>
              )}
            </button>
          )}
          <button
            onClick={onManual}
            disabled={analyzing}
            style={{
              flex: 1, minWidth: '120px', padding: '12px 16px',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border)',
              background: 'var(--bg-card)', color: 'var(--text-secondary)',
              fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: '600',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            }}
          >
            ✍️ Escribir manual
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>(gratis)</span>
          </button>
        </div>

      </div>
    </div>
  );
}


function StepUpload({ onFile, file, preview, projects = [], selectedProject, setSelectedProject }) {
  const [uploadTab, setUploadTab] = useState('upload');
  const MEDIA_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1').replace('/api/v1', '');
  const onDrop = useCallback((accepted) => { if (accepted[0]) onFile(accepted[0]); }, [onFile]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { 'image/*': [], 'video/*': [] }, maxSize: 500 * 1024 * 1024, multiple: false,
  });

  const handleSelectFromLibrary = async (item) => {
    try {
      const url = `${MEDIA_BASE}${item.media_url}`;
      const res = await fetch(url);
      const blob = await res.blob();
      const ext = item.filename.split('.').pop();
      const mimeType = item.media_type === 'video' ? `video/${ext}` : `image/${ext}`;
      const fileObj = new File([blob], item.filename, { type: mimeType });
      onFile(fileObj);
      setUploadTab('upload');
    } catch { toast.error('Error al cargar desde biblioteca'); }
  };

  return (
    <div className="animate-fadeIn" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* Tabs subir / biblioteca */}
      <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-elevated)', padding: '4px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
        {[{ id: 'upload', label: '📤 Subir archivo' }, { id: 'library', label: '🖼 Mi biblioteca' }].map(t => (
          <button key={t.id} onClick={() => setUploadTab(t.id)} style={{ flex: 1, padding: '8px 12px', borderRadius: 'var(--radius-md)', border: 'none', background: uploadTab === t.id ? 'var(--bg-card)' : 'transparent', color: uploadTab === t.id ? 'var(--text-primary)' : 'var(--text-muted)', fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: uploadTab === t.id ? '700' : '500', cursor: 'pointer', transition: 'all 0.2s', boxShadow: uploadTab === t.id ? 'var(--shadow-sm)' : 'none' }}>
            {t.label}
          </button>
        ))}
      </div>

      {uploadTab === 'library' && (
        <MediaLibraryPage selectable onSelect={handleSelectFromLibrary} />
      )}

      {uploadTab === 'upload' && (
      <div {...getRootProps()} style={{
        border: `2px dashed ${isDragActive ? '#7c3aed' : file ? '#10b981' : 'var(--border)'}`,
        borderRadius: 'var(--radius-xl)', padding: '48px 24px', textAlign: 'center',
        cursor: 'pointer', transition: 'all 0.3s',
        background: isDragActive ? '#7c3aed10' : file ? '#10b98110' : 'var(--bg-elevated)',
      }}>
        <input {...getInputProps()} />
        {preview ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            {file?.type?.startsWith('video') ? (
              <video src={preview} style={{ maxHeight: '240px', maxWidth: '100%', borderRadius: 'var(--radius-lg)' }} controls />
            ) : (
              <img src={preview} alt="preview" style={{ maxHeight: '240px', maxWidth: '100%', borderRadius: 'var(--radius-lg)', objectFit: 'contain' }} />
            )}
            <Badge color="#10b981">✓ {file?.name} ({(file?.size / 1024 / 1024).toFixed(1)} MB)</Badge>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Arrastra otra para reemplazar</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <div className="animate-float" style={{ fontSize: '52px' }}>📤</div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: '700', color: isDragActive ? '#a78bfa' : 'var(--text-primary)' }}>
              {isDragActive ? '¡Suelta aquí!' : 'Arrastra tu imagen o video'}
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>O haz clic para seleccionar · Máx 500MB</p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
              {['JPG', 'PNG', 'WEBP', 'GIF', 'MP4', 'MOV', 'WEBM'].map(f => <Badge key={f} color="#555">{f}</Badge>)}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Selector de proyecto */}
      <div style={{ marginTop: '20px', width: '100%' }}>
        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
          Proyecto <span style={{ color: 'var(--text-muted)', fontWeight: '400' }}>(opcional)</span>
        </label>
        <select
          value={selectedProject || ''}
          onChange={e => setSelectedProject(e.target.value)}
          style={{
            width: '100%', padding: '10px 14px', borderRadius: 'var(--radius-md)',
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            color: selectedProject ? 'var(--text-primary)' : 'var(--text-muted)',
            fontSize: '14px', fontFamily: 'var(--font-body)', outline: 'none', cursor: 'pointer',
          }}
        >
          <option value="">Sin proyecto — la IA usará contexto genérico</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {selectedProject && projects.find(p => p.id === selectedProject) && (
          <p style={{ fontSize: '12px', color: 'var(--accent-primary)', marginTop: '6px' }}>
            ✓ La IA generará contenido con el tono y contexto de <strong>{projects.find(p => p.id === selectedProject)?.name}</strong>
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Step 1: AI Analysis ─────────────────────────────────
function StepAnalysis({ file, analyzing, analysis, generatingCaptions }) {
  const [dotCount, setDotCount] = React.useState(0);
  React.useEffect(() => {
    if (!generatingCaptions) return;
    const t = setInterval(() => setDotCount(d => (d + 1) % 4), 500);
    return () => clearInterval(t);
  }, [generatingCaptions]);

  const steps = [
    { label: 'Leyendo archivo', done: true },
    { label: 'Analizando contenido visual', done: !!analysis || !analyzing },
    { label: 'Detectando audiencia y tono', done: !!analysis },
    { label: 'Identificando redes óptimas', done: !!analysis },
    { label: 'Generando contenido personalizado', done: !!analysis && !generatingCaptions, loading: generatingCaptions },
  ];

  return (
    <div className="animate-fadeIn" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px', padding: '20px 0' }}>
      <div style={{ fontSize: '56px', className: 'animate-float' }}>🤖</div>
      <div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: '800', textAlign: 'center', marginBottom: '6px' }}>
          {analysis ? '¡Análisis completado!' : 'La IA está analizando tu contenido...'}
        </h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px', textAlign: 'center' }}>Esto toma unos segundos</p>
      </div>

      <div style={{ width: '100%', maxWidth: '400px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {steps.map((step, i) => (
          <div key={i} className={`animate-fadeIn delay-${i + 1}`} style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            padding: '12px 16px', borderRadius: 'var(--radius-md)',
            background: step.loading ? '#7c3aed10' : 'var(--bg-elevated)',
            border: `1px solid ${step.loading ? '#7c3aed40' : 'var(--border)'}`,
            transition: 'all 0.3s',
          }}>
            <div style={{ width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {step.done ? <span style={{ color: '#10b981', fontSize: '14px' }}>✓</span> : step.loading ? <Spinner size={16} /> : <Spinner size={16} />}
            </div>
            <span style={{ fontSize: '13px', color: step.done ? 'var(--text-primary)' : step.loading ? '#a78bfa' : 'var(--text-muted)', fontWeight: step.loading ? '600' : '400' }}>
              {step.label}{step.loading ? '.'.repeat(dotCount + 1) : ''}
            </span>
          </div>
        ))}
      </div>

      {analysis && generatingCaptions && (
        <div className="animate-scaleIn" style={{ width: '100%', maxWidth: '400px', background: 'linear-gradient(135deg, #7c3aed10, #a855f710)', border: '1px solid #7c3aed40', borderRadius: 'var(--radius-lg)', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {['Instagram','Twitter','LinkedIn','Facebook','YouTube'].map((net, i) => (
              <div key={net} style={{
                padding: '4px 10px', borderRadius: '99px', fontSize: '11px', fontWeight: '700',
                background: '#7c3aed20', border: '1px solid #7c3aed40', color: '#a78bfa',
                animation: `pulse 1.5s ease-in-out ${i * 0.2}s infinite`,
              }}>{net}</div>
            ))}
          </div>
          <div style={{ fontSize: '13px', color: '#a78bfa', fontWeight: '600', textAlign: 'center' }}>
            {'✨ Creando captions personalizados para cada red' + '.'.repeat(dotCount + 1)}
          </div>
          <div style={{ width: '100%', height: '4px', background: 'var(--bg-hover)', borderRadius: '99px', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: '99px',
              background: 'linear-gradient(90deg, #7c3aed, #a855f7, #7c3aed)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s linear infinite',
              width: '60%',
            }} />
          </div>
          <style>{`
            @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
            @keyframes pulse { 0%,100% { opacity:0.5; transform:scale(1); } 50% { opacity:1; transform:scale(1.05); } }
          `}</style>
        </div>
      )}

      {analysis && !generatingCaptions && (
        <div className="animate-scaleIn" style={{ width: '100%', maxWidth: '400px', background: 'var(--bg-elevated)', border: '1px solid #7c3aed40', borderRadius: 'var(--radius-lg)', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>🎯</span>
            <span style={{ fontSize: '13px', fontWeight: '600' }}>Tipo detectado:</span>
            <Badge color="#7c3aed">{analysis.type === 'video' ? '🎬 Video' : '🖼 Imagen'}</Badge>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>💡</span>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{analysis.description}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '16px' }}>✨</span>
            <span style={{ fontSize: '13px', fontWeight: '600' }}>Mood:</span>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{analysis.mood}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step 2: Content Editor ──────────────────────────────
function HashtagInput({ value, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!loaded) {
      hashtagsAPI.history().then(r => setSuggestions(r.data.map(h => h.tag))).catch(() => {});
      setLoaded(true);
    }
  }, [loaded]);

  const tags = (value || '').split(' ').filter(Boolean);
  const lastWord = (value || '').split(' ').pop();
  const filtered = lastWord.startsWith('#') && lastWord.length > 1
    ? suggestions.filter(s => s.toLowerCase().includes(lastWord.toLowerCase()) && !tags.includes(s))
    : [];

  return (
    <div style={{ position: 'relative' }}>
      <Input
        label="Hashtags"
        value={value || ''}
        onChange={onChange}
        placeholder="#tag1 #tag2 #tag3"
        icon="#"
      />
      {filtered.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', zIndex: 50, maxHeight: '150px', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' }}>
          {filtered.slice(0, 8).map(tag => (
            <div
              key={tag}
              onClick={() => {
                const parts = (value || '').split(' ');
                parts[parts.length - 1] = tag;
                onChange(parts.join(' ') + ' ');
              }}
              style={{ padding: '8px 12px', fontSize: '13px', cursor: 'pointer', color: 'var(--accent-primary)', fontWeight: '600' }}
              onMouseEnter={e => e.target.style.background = 'var(--bg-hover)'}
              onMouseLeave={e => e.target.style.background = 'transparent'}
            >
              {tag}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CaptionTemplatePanel({ network, onApply }) {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen]           = useState(false);
  const [saving, setSaving]       = useState(false);
  const [newName, setNewName]     = useState('');
  const [newCaption, setNewCaption] = useState('');
  const [newHashtags, setNewHashtags] = useState('');

  const load = () => captionTemplatesAPI.list(network).then(r => setTemplates(r.data)).catch(() => {});

  useEffect(() => { load(); }, [network]);

  const handleSave = async () => {
    if (!newName.trim() || !newCaption.trim()) { toast.error('Nombre y caption son requeridos'); return; }
    setSaving(true);
    try {
      await captionTemplatesAPI.create({ name: newName, caption: newCaption, hashtags: newHashtags, network });
      setNewName(''); setNewCaption(''); setNewHashtags('');
      load(); toast.success('Plantilla guardada ✓');
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await captionTemplatesAPI.delete(id);
    load();
  };

  return (
    <div style={{ marginTop: '8px' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{ background: 'none', border: 'none', color: 'var(--accent-primary)', fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: 0 }}
      >
        {open ? '▲ Cerrar plantillas' : '▼ Mis plantillas de caption'}
      </button>

      {open && (
        <div className="animate-fadeIn" style={{ marginTop: '10px', padding: '14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* Guardar nueva */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <p style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', margin: 0 }}>Guardar caption actual como plantilla:</p>
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nombre de la plantilla" style={{ padding: '7px 10px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none' }} />
            <textarea value={newCaption} onChange={e => setNewCaption(e.target.value)} placeholder="Caption..." rows={3} style={{ padding: '7px 10px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none', resize: 'vertical' }} />
            <input value={newHashtags} onChange={e => setNewHashtags(e.target.value)} placeholder="#hashtag1 #hashtag2" style={{ padding: '7px 10px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none' }} />
            <button onClick={handleSave} disabled={saving} style={{ padding: '7px 14px', borderRadius: '8px', background: 'var(--gradient-primary)', border: 'none', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer', alignSelf: 'flex-end' }}>
              {saving ? 'Guardando...' : '💾 Guardar'}
            </button>
          </div>

          {/* Lista de plantillas */}
          {templates.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid var(--border)', paddingTop: '10px' }}>
              <p style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', margin: 0 }}>Aplicar plantilla:</p>
              {templates.map(t => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>{t.name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.caption}</div>
                  </div>
                  <button onClick={() => onApply(t)} style={{ background: '#7c3aed20', border: '1px solid #7c3aed40', color: '#a78bfa', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontFamily: 'var(--font-body)', fontWeight: '600', flexShrink: 0 }}>Usar</button>
                  <button onClick={() => handleDelete(t.id)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '14px', flexShrink: 0 }}>✕</button>
                </div>
              ))}
            </div>
          )}
          {templates.length === 0 && <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>Sin plantillas guardadas para esta red.</p>}
        </div>
      )}
    </div>
  );
}

function StepContent({ analysis, editedContent, setEditedContent, selectedProject, setSelectedProject, tone, setTone, onRegenerate, regenerating, projects, manualMode, creditExhausted }) {
  const [activeNet, setActiveNet] = useState(analysis?.bestNetworks?.[0] || 'instagram');

  return (
    <div className="animate-fadeIn" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Project + Tone */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Proyecto</label>
          <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', fontSize: '14px', color: selectedProject ? 'var(--text-primary)' : 'var(--text-muted)' }}>
            {selectedProject ? (projects.find(p => p.id === selectedProject)?.name || 'Proyecto') : 'Sin proyecto'}
          </div>
        </div>
        <div>
          <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Tono</label>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {TONES.map(t => (
              <button key={t.id} onClick={() => setTone(t.id)} style={{ padding: '7px 10px', borderRadius: '8px', border: `1px solid ${tone === t.id ? '#7c3aed' : 'var(--border)'}`, background: tone === t.id ? '#7c3aed20' : 'var(--bg-elevated)', color: tone === t.id ? '#a78bfa' : 'var(--text-muted)', fontSize: '12px', fontFamily: 'var(--font-body)', cursor: 'pointer', transition: 'all 0.2s' }}>{t.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Network tabs */}
      <div>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', overflowX: 'auto', paddingBottom: '4px' }}>
          {analysis?.bestNetworks?.map(nid => {
            const cfg = NETWORK_CONFIG[nid];
            return (
              <button key={nid} onClick={() => setActiveNet(nid)} style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '7px 14px', borderRadius: 'var(--radius-md)',
                border: `1px solid ${activeNet === nid ? cfg?.color : 'var(--border)'}`,
                background: activeNet === nid ? `${cfg?.color}20` : 'var(--bg-elevated)',
                color: activeNet === nid ? cfg?.color : 'var(--text-muted)',
                fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: '600',
                cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                <NetworkIcon network={nid} size={18} />
                {cfg?.name}
              </button>
            );
          })}
        </div>

        {/* Content editor */}
        {editedContent[activeNet] && (
          <div className="animate-fadeIn" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                Caption para {NETWORK_CONFIG[activeNet]?.name}
                <span style={{ color: 'var(--text-muted)', fontWeight: '400', marginLeft: '8px' }}>
                  ({editedContent[activeNet]?.caption?.length || 0} caracteres)
                </span>
              </label>
              <textarea
                value={editedContent[activeNet]?.caption || ''}
                onChange={e => setEditedContent(prev => ({ ...prev, [activeNet]: { ...prev[activeNet], caption: e.target.value } }))}
                rows={5}
                style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', color: 'var(--text-primary)', fontFamily: 'var(--font-body)', fontSize: '14px', padding: '12px', outline: 'none', resize: 'vertical', lineHeight: 1.6 }}
              />
            </div>
            <HashtagInput
              value={editedContent[activeNet]?.hashtags || ''}
              onChange={v => setEditedContent(prev => ({ ...prev, [activeNet]: { ...prev[activeNet], hashtags: v } }))}
            />
            <CaptionTemplatePanel
              network={activeNet}
              onApply={t => setEditedContent(prev => ({ ...prev, [activeNet]: { ...prev[activeNet], caption: t.caption, hashtags: t.hashtags || '' } }))}
            />
          </div>
        )}
      </div>

      {/* Banner modo manual */}
      {manualMode && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '14px',
          padding: '16px 18px', borderRadius: 'var(--radius-lg)',
          background: 'linear-gradient(135deg, #f59e0b10, #f59e0b05)',
          border: '1px solid #f59e0b40',
        }}>
          <span style={{ fontSize: '22px', flexShrink: 0, marginTop: '2px' }}>⚠️</span>
          <div>
            <div style={{ fontWeight: '700', fontSize: '14px', color: '#f59e0b', marginBottom: '4px' }}>
              Sin proveedor de IA configurado
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              No se encontró un proveedor de IA activo, por lo que el contenido no fue generado automáticamente.
              Puedes redactar manualmente el caption y los hashtags para cada red social.
              Para habilitar la generación automática, activa un proveedor en{' '}
              <a href="/providers" style={{ color: 'var(--accent-primary)', fontWeight: '600', textDecoration: 'none' }}>
                Proveedores de IA →
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate — solo si hay IA disponible */}
      {!manualMode && (
        creditExhausted ? (
          <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: '#ef444410', border: '1px solid #ef444430', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🔒</span>
            <span style={{ fontSize: '12px', color: '#ef4444', fontWeight: '600' }}>Límite mensual de IA alcanzado — actualiza tu plan para regenerar</span>
          </div>
        ) : (
          <Button variant="secondary" icon="🔄" loading={regenerating} onClick={onRegenerate}>
            Regenerar con IA
          </Button>
        )
      )}
    </div>
  );
}

// ─── Step 3: Networks & Destination ─────────────────────
function StepNetworks({ accounts, selectedNetworks, setSelectedNetworks, selectedDestinations, setSelectedDestinations, file, includeStory, setIncludeStory }) {
  const isVideo = file?.type?.startsWith('video');
  const fileSizeMB = file ? file.size / 1024 / 1024 : 0;
  const twitterSelected = selectedNetworks.some(id => accounts.find(a => a.id === id && a.network === 'twitter'));
  const twitterSizeWarning = twitterSelected && !isVideo && fileSizeMB > 5;

  const isTwitterFileTooLarge = (acc) => acc.network === 'twitter' && !isVideo && fileSizeMB > 5;

  // Auto-deselect Twitter if file exceeds 5MB
  React.useEffect(() => {
    if (fileSizeMB > 5 && !isVideo) {
      const twitterIds = accounts.filter(a => a.network === 'twitter').map(a => a.id);
      if (twitterIds.some(id => selectedNetworks.includes(id))) {
        setSelectedNetworks(prev => prev.filter(id => !twitterIds.includes(id)));
      }
    }
  }, [fileSizeMB, isVideo]);

  const toggle = (accountId) => {
    const acc = accounts.find(a => a.id === accountId);
    if (acc && isTwitterFileTooLarge(acc)) return;
    setSelectedNetworks(prev => prev.includes(accountId) ? prev.filter(x => x !== accountId) : [...prev, accountId]);
  };

  return (
    <div className="animate-fadeIn" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Selecciona en qué cuentas publicar. Puedes elegir múltiples.</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {accounts.map((acc, i) => {
          const cfg = NETWORK_CONFIG[acc.network];
          const supportsMedia = isVideo ? cfg?.supports?.includes('video') : cfg?.supports?.includes('image');
          const selected = selectedNetworks.includes(acc.id);
          const twitterBlocked = isTwitterFileTooLarge(acc);
          const isDisabled = !acc.is_connected || !supportsMedia || twitterBlocked;

          return (
            <div key={acc.id} className={`animate-fadeIn delay-${i + 1}`} style={{
              display: 'flex', alignItems: 'center', gap: '14px',
              padding: '14px 16px', borderRadius: 'var(--radius-lg)',
              border: `1px solid ${twitterBlocked ? '#ef444460' : selected ? cfg?.color + '60' : acc.is_connected ? 'var(--border)' : '#ef444430'}`,
              background: twitterBlocked ? '#ef444408' : selected ? `${cfg?.color}10` : 'var(--bg-elevated)',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: !acc.is_connected || !supportsMedia ? 0.5 : 1,
              transition: 'all 0.2s',
            }} onClick={() => !isDisabled && toggle(acc.id)}>
              <NetworkIcon network={acc.network} size={36} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>{acc.name}</div>
                <div style={{ fontSize: '12px', color: twitterBlocked ? '#ef4444' : 'var(--text-muted)', marginTop: '2px' }}>
                  {twitterBlocked ? `⛔ Imagen supera el límite de 5 MB de X (${fileSizeMB.toFixed(1)} MB)` : !acc.is_connected ? '⚠️ No conectado' : !supportsMedia ? `No soporta ${isVideo ? 'videos' : 'imágenes'}` : `${acc.followers?.toLocaleString('es') || 0} seguidores · ${acc.type}`}
                </div>
              </div>
              {acc.is_connected && supportsMedia && (
                <div style={{
                  width: '22px', height: '22px', borderRadius: '50%',
                  border: `2px solid ${selected ? cfg?.color : 'var(--border)'}`,
                  background: selected ? cfg?.color : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: '12px', flexShrink: 0, transition: 'all 0.2s',
                }}>{selected ? '✓' : ''}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Story option */}
      <div style={{ padding: '14px 16px', borderRadius: 'var(--radius-lg)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>📱 También publicar como Story</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Recibirás una notificación push para publicar en Instagram y TikTok</div>
        </div>
        <Toggle checked={includeStory} onChange={setIncludeStory} />
      </div>
    </div>
  );
}

// ─── Step 4: Publish & Result ────────────────────────────
const MEDIA_BASE_PREVIEW = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1').replace('/api/v1', '');

const NET_LIMITS = {
  instagram: { caption: 2200 },
  tiktok:    { caption: 2200 },
  twitter:   { caption: 280 },
  linkedin:  { caption: 3000 },
  facebook:  { caption: 63206 },
  youtube:   { caption: 5000 },
};

function NetworkPreview({ account, content, mediaUrl, mediaType }) {
  const [imgErr, setImgErr] = useState(false);
  const cfg     = NETWORK_CONFIG[account.network] || {};
  const limits  = NET_LIMITS[account.network] || { caption: 2200 };
  const caption = content?.caption || '';
  const hashtags= content?.hashtags || '';
  const charCount = caption.length;
  const overLimit = charCount > limits.caption;
  const url = mediaUrl ? `${MEDIA_BASE_PREVIEW}${mediaUrl}` : null;

  return (
    <div style={{ border: `1px solid ${cfg.color || 'var(--border)'}40`, borderRadius: 'var(--radius-lg)', overflow: 'hidden', background: 'var(--bg-elevated)' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px', background: `${cfg.color || '#888'}15` }}>
        <NetworkIcon network={account.network} size={20} />
        <span style={{ fontWeight: '700', fontSize: '13px' }}>{account.name}</span>
        <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: 'auto' }}>{cfg.name}</span>
      </div>
      {url && !imgErr && (
        <div style={{ background: '#000', maxHeight: '180px', overflow: 'hidden' }}>
          {mediaType === 'video'
            ? <video src={url} style={{ width: '100%', maxHeight: '180px', objectFit: 'cover', display: 'block' }} muted onError={() => setImgErr(true)} />
            : <img src={url} alt="preview" onError={() => setImgErr(true)} style={{ width: '100%', maxHeight: '180px', objectFit: 'cover', display: 'block' }} />
          }
        </div>
      )}
      <div style={{ padding: '12px 14px' }}>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.55, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '80px', overflow: 'hidden' }}>
          {caption || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Sin caption</span>}
        </p>
        {hashtags && <p style={{ fontSize: '12px', color: cfg.color || 'var(--accent-primary)', marginTop: '6px', wordBreak: 'break-word' }}>{hashtags}</p>}
        <div style={{ marginTop: '8px' }}>
          <span style={{ fontSize: '11px', color: overLimit ? '#ef4444' : 'var(--text-muted)' }}>
            {charCount}/{limits.caption} {overLimit && '⚠️ Límite excedido'}
          </span>
        </div>
      </div>
    </div>
  );
}

function StepPublish({ selectedNetworks, accounts, publishing, published, publishResults, onPublish, scheduledAt, setScheduledAt, editedContent, analysis, uploadedMediaId }) {
  const selectedAccs = accounts.filter(a => selectedNetworks.includes(a.id));

  if (published) {
    return (
      <div className="animate-scaleIn" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', padding: '20px 0' }}>
        <div style={{ fontSize: '64px' }}>🎉</div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: '800', textAlign: 'center' }}>¡Publicado exitosamente!</h3>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {selectedAccs.map((acc, i) => {
            const result = publishResults?.[acc.id];
            const cfg = NETWORK_CONFIG[acc.network];
            return (
              <div key={acc.id} className={`animate-fadeIn delay-${i + 1}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
                <NetworkIcon network={acc.network} size={28} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '13px' }}>{acc.name}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                  <Badge color={result?.success ? '#10b981' : result?.scheduled ? '#3b82f6' : '#ef4444'}>
                    {result?.success ? '✓ Publicado' : result?.scheduled ? '⏰ Programado' : '✕ Error'}
                  </Badge>
                  {!result?.success && result?.error && (
                    <span style={{ fontSize: '11px', color: '#ef4444', maxWidth: '180px', textAlign: 'right', lineHeight: 1.4 }}>
                      {result.error.includes('5242880') || result.error.includes('maxFileSizeExceeded') || result.error.includes('File size')
                        ? 'Imagen supera el límite de 5 MB de X'
                        : result.error.length > 60 ? result.error.slice(0, 60) + '…' : result.error}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '17px', fontWeight: '700' }}>Vista previa y resumen</h3>

      {/* Vista previa por red */}
      {selectedAccs.length > 0 && analysis && (
        <div>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '10px' }}>
            Así se verá tu publicación en cada red:
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
            {selectedAccs.map(acc => (
              <NetworkPreview
                key={acc.id}
                account={acc}
                content={editedContent[acc.network]}
                mediaUrl={uploadedMediaId ? `/uploads/${uploadedMediaId}` : null}
                mediaType={analysis?.type}
              />
            ))}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {selectedAccs.map((acc, i) => {
          const cfg = NETWORK_CONFIG[acc.network];
          return (
            <div key={acc.id} className={`animate-fadeIn delay-${i + 1}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
              <NetworkIcon network={acc.network} size={28} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '13px' }}>{acc.name}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{cfg?.name} · {acc.type}</div>
              </div>
              {publishing ? <Spinner size={18} /> : <Badge color={cfg?.color}>Listo</Badge>}
            </div>
          );
        })}
      </div>

      {selectedAccs.length === 0 && (
        <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '14px' }}>
          ⚠️ No has seleccionado ninguna red social
        </div>
      )}

      {selectedAccs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Selector de fecha/hora */}
          <div style={{ padding: '16px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontSize: '13px', fontWeight: '600' }}>📅 Programar publicación</span>
              {scheduledAt && (
                <button onClick={() => setScheduledAt('')} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px' }}>✕ Cancelar</button>
              )}
            </div>
            <input
              type="datetime-local"
              value={scheduledAt}
              min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
              onChange={e => setScheduledAt(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)',
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                color: 'var(--text-primary)', fontSize: '14px', fontFamily: 'var(--font-body)',
                outline: 'none', colorScheme: 'dark',
              }}
            />
            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
              {scheduledAt ? `Se publicará el ${new Date(scheduledAt).toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' })}` : 'Dejar vacío para publicar inmediatamente'}
            </p>
          </div>

          <Button size="lg" loading={publishing} onClick={onPublish} style={{ width: '100%' }} icon={scheduledAt ? '📅' : '🚀'}>
            {publishing
              ? (scheduledAt ? 'Programando...' : 'Publicando en todas las redes...')
              : scheduledAt
                ? `Programar para ${new Date(scheduledAt).toLocaleString('es', { dateStyle: 'short', timeStyle: 'short' })}`
                : `Publicar ahora en ${selectedAccs.length} cuenta${selectedAccs.length > 1 ? 's' : ''}`
            }
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Main Wizard ─────────────────────────────────────────
export default function PublishWizard() {
  const navigate = useNavigate();
  const { accounts, fetchAccounts, addPost, projects, fetchProjects } = useAppStore();
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [editedContent, setEditedContent] = useState({});
  const [selectedProject, setSelectedProject] = useState('');
  const [tone, setTone] = useState('profesional');
  const [regenerating, setRegenerating] = useState(false);
  const [selectedNetworks, setSelectedNetworks] = useState([]);
  const [selectedDestinations, setSelectedDestinations] = useState({});
  const [includeStory, setIncludeStory] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [publishResults, setPublishResults] = useState({});
  const [uploadedMediaId, setUploadedMediaId] = useState(null);
  const [scheduledAt, setScheduledAt] = useState('');
  const [youtubeOptions, setYoutubeOptions] = useState({ privacy_status: 'public', made_for_kids: false, category_id: '22', contains_ai: false, title: '', description: '' });
  const [youtubeThumbnail, setYoutubeThumbnail] = useState(null);
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [realTokens, setRealTokens] = useState(null);
  const [generatingCaptions, setGeneratingCaptions] = useState(false);
  const [activeProvider, setActiveProvider] = useState(null);

  React.useEffect(() => {
    fetchAccounts();
    fetchProjects();
    // Cargar suscripción y proveedor activo
    billingAPI.getSubscription().then(r => setSubscription(r.data)).catch(() => {});
    import('../api/client').then(({ default: _, ...mod }) => {
      const api = mod.postsAPI || mod.billingAPI;
    });
    providersAPI.list().then(r => {
      const active = Array.isArray(r.data) ? r.data.find(p => p.is_active) : null;
      setActiveProvider(active);
    }).catch(() => {});
  }, []);

  const handleFile = (f) => {
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setAnalysis(null); setSelectedNetworks([]); setPublished(false); setRealTokens(null);
  };

  const handleManual = () => {
    setManualMode(true);
    const isVideo = file?.type?.startsWith('video');
    const defaultNetworks = isVideo
      ? ['instagram', 'tiktok', 'twitter', 'linkedin', 'facebook', 'youtube']
      : ['instagram', 'twitter', 'linkedin', 'facebook'];
    const result = { type: isVideo ? 'video' : 'image', description: '', mood: '', bestNetworks: defaultNetworks };
    setAnalysis(result);
    const emptyContent = {};
    defaultNetworks.forEach(n => { emptyContent[n] = { caption: '', hashtags: '' }; });
    setEditedContent(emptyContent);
    const connectedNets = accounts.filter(a => a.is_connected && defaultNetworks.includes(a.network)).map(a => a.id);
    setSelectedNetworks(connectedNets);
    // Upload el archivo en background para tenerlo listo al publicar
    if (file && !uploadedMediaId) {
      const fd = new FormData(); fd.append('file', file);
      postsAPI.upload(fd).then(r => setUploadedMediaId(r.data.media_id)).catch(() => {});
    }
    setStep(2);
  };

  const runAnalysis = async () => {
    if (!file) return;
    setAnalyzing(true);
    setManualMode(false);
    setStep(1);
    try {
      // 1. Subir archivo
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await postsAPI.upload(formData);
      const mediaId = uploadRes.data.media_id;
      setUploadedMediaId(mediaId);

      const mediaType = uploadRes.data.media_type;
      const defaultNetworks = mediaType === 'video'
        ? ['instagram', 'tiktok', 'twitter', 'linkedin', 'facebook', 'youtube']
        : ['instagram', 'twitter', 'linkedin', 'facebook'];

      try {
        // 2. Analizar con IA
        const analyzeRes = await postsAPI.analyze(mediaId, selectedProject || null);
        const apiAnalysis = analyzeRes.data;
        if (apiAnalysis.tokens_in !== undefined) {
          setRealTokens({ tokens_in: apiAnalysis.tokens_in, tokens_out: apiAnalysis.tokens_out });
        }
        // Normalizar redes: minúsculas y mapear nombres completos a slugs
        const NETWORK_SLUG_MAP = {
          'linkedin': 'linkedin', 'x (twitter)': 'twitter', 'twitter': 'twitter',
          'instagram': 'instagram', 'facebook': 'facebook', 'tiktok': 'tiktok',
          'youtube': 'youtube', 'x': 'twitter',
        };
        const rawNetworks = apiAnalysis.best_networks || defaultNetworks;
        const normalizedNetworks = rawNetworks
          .map(n => NETWORK_SLUG_MAP[n.toLowerCase()] || n.toLowerCase())
          .filter((n, i, arr) => arr.indexOf(n) === i); // deduplicar
        const result = {
          type: mediaType,
          description: apiAnalysis.description || '',
          mood: apiAnalysis.mood || '',
          bestNetworks: normalizedNetworks.length > 0 ? normalizedNetworks : defaultNetworks,
        };
        setAnalysis(result);

        // 3. Generar contenido
        setGeneratingCaptions(true);
        const genRes = await postsAPI.generate({
          media_id: mediaId,
          project_id: selectedProject || null,
          networks: result.bestNetworks,
          tone,
        });
        setGeneratingCaptions(false);
        setEditedContent(genRes.data.content || {});
        const connectedNets = accounts.filter(a => a.is_connected && result.bestNetworks.includes(a.network)).map(a => a.id);
        setSelectedNetworks(connectedNets);

      } catch (aiError) {
        setGeneratingCaptions(false);
        // Sin proveedor IA: modo manual
        const isNoProvider = aiError?.response?.status === 400;
        setManualMode(true);
        const result = { type: mediaType, description: '', mood: '', bestNetworks: defaultNetworks };
        setAnalysis(result);
        const emptyContent = {};
        defaultNetworks.forEach(n => { emptyContent[n] = { caption: '', hashtags: '' }; });
        setEditedContent(emptyContent);
        const connectedNets = accounts.filter(a => a.is_connected && defaultNetworks.includes(a.network)).map(a => a.id);
        setSelectedNetworks(connectedNets);
      }

      setStep(2);
    } catch (e) {
      toast.error('Error al subir el archivo. Intenta de nuevo.');
      setStep(0);
    }
    setAnalyzing(false);
  };

  const handleRegenerate = async () => {
    if (!uploadedMediaId || manualMode) return;
    setRegenerating(true);
    try {
      const genRes = await postsAPI.generate({
        media_id: uploadedMediaId,
        project_id: selectedProject || null,
        networks: analysis.bestNetworks,
        tone,
      });
      setEditedContent(genRes.data.content || {});
      toast.success('Contenido regenerado con IA ✨');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al regenerar');
    }
    setRegenerating(false);
  };

  const handlePublish = async () => {
    // Si hay cuenta YouTube seleccionada, mostrar modal primero
    const selectedAccs_ = accounts.filter(a => selectedNetworks.includes(a.id));
    const hasYoutube = selectedAccs_.some(a => a.network === 'youtube');
    if (hasYoutube && !showYoutubeModal) {
      setShowYoutubeModal(true);
      return;
    }
    setShowYoutubeModal(false);
    setPublishing(true);
    try {
      // 1. Usar media_id del análisis previo (ya fue subido en runAnalysis)
      const mediaId = uploadedMediaId;
      if (!mediaId) throw new Error('No hay archivo subido');

      // 2. Build networks payload
      const selectedAccs = accounts.filter(a => selectedNetworks.includes(a.id));
      // Convert thumbnail to base64 if present
      let thumbB64 = null;
      if (youtubeThumbnail) {
        thumbB64 = await new Promise(res => {
          const r = new FileReader(); r.onload = () => res(r.result.split(',')[1]); r.readAsDataURL(youtubeThumbnail);
        });
      }
      const networks = selectedAccs.map(acc => {
        const net = acc.network;
        const netContent = editedContent[net] || {};
        return {
          account_id: acc.id,
          caption: netContent.caption || '',
          hashtags: netContent.hashtags || '',
          ...(net === 'youtube' ? { youtube_options: { ...youtubeOptions, thumbnail_b64: thumbB64 } } : {}),
        };
      });

      // 3. Publish
      const publishRes = await postsAPI.publish({
        media_id: mediaId,
        project_id: selectedProject || null,
        networks,
        tone,
        include_story: includeStory,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      });

      // 4. Build results map
      const results = {};
      const postNetworks = publishRes.data.networks || [];
      selectedAccs.forEach(acc => {
        const netResult = postNetworks.find(n => n.network === acc.network);
        const isScheduled = netResult?.status === 'scheduled';
        results[acc.id] = { success: netResult?.status === 'published', scheduled: isScheduled, error: netResult?.error_message };
      });

      setPublishResults(results);
      setPublished(true);
      setStep(4);
      addPost(publishRes.data);
      const okCount = Object.values(results).filter(r => r.success).length;
      toast.success(`¡Publicado en ${okCount} de ${selectedAccs.length} redes! 🚀`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al publicar');
    } finally {
      setPublishing(false);
    }
  };

  const canNext = () => {
    if (step === 0) return !!file;
    if (step === 2) return true;
    if (step === 3) return selectedNetworks.length > 0;
    return true;
  };

  const handleNext = () => {
    if (step === 0) { runAnalysis(); return; }
    if (step < 4) setStep(s => s + 1);
  };

  const reset = () => { setStep(0); setFile(null); setPreview(null); setAnalysis(null); setSelectedNetworks([]); setPublished(false); setPublishResults({}); };

  return (
    <>
    <div style={{ maxWidth: '680px', margin: '0 auto' }}>
      <StepIndicator current={step} />

      <Card style={{ minHeight: '400px' }}>
        {step === 0 && (
          <>
            <StepUpload onFile={handleFile} file={file} preview={preview} projects={projects} selectedProject={selectedProject} setSelectedProject={setSelectedProject} />
            {file && (
              <AIReadinessPanel
                file={file}
                subscription={subscription}
                activeProvider={activeProvider}
                onAnalyze={runAnalysis}
                onManual={handleManual}
                analyzing={analyzing}
              />
            )}
          </>
        )}
        {step === 1 && <StepAnalysis file={file} analyzing={analyzing} analysis={analysis} realTokens={realTokens} generatingCaptions={generatingCaptions} />}
        {step === 2 && <StepContent analysis={analysis} editedContent={editedContent} setEditedContent={setEditedContent} selectedProject={selectedProject} setSelectedProject={setSelectedProject} tone={tone} setTone={setTone} onRegenerate={handleRegenerate} regenerating={regenerating} projects={projects} manualMode={manualMode} creditExhausted={subscription && subscription.ai_credit_limit > 0 && (subscription.ai_credit_limit + subscription.ai_credit_rollover - subscription.ai_credit_used) <= 0} />}
        {step === 3 && <StepNetworks accounts={accounts} selectedNetworks={selectedNetworks} setSelectedNetworks={setSelectedNetworks} selectedDestinations={selectedDestinations} setSelectedDestinations={setSelectedDestinations} file={file} includeStory={includeStory} setIncludeStory={setIncludeStory} />}
        {step === 4 && <StepPublish selectedNetworks={selectedNetworks} accounts={accounts} publishing={publishing} published={published} publishResults={publishResults} onPublish={handlePublish} scheduledAt={scheduledAt} setScheduledAt={setScheduledAt} editedContent={editedContent} analysis={analysis} uploadedMediaId={uploadedMediaId} />}
      </Card>

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
        {published ? (
          <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
            <Button variant="secondary" onClick={reset} style={{ flex: 1 }}>Nueva publicación</Button>
            <Button onClick={() => navigate('/posts')} style={{ flex: 1 }}>Ver publicaciones →</Button>
          </div>
        ) : (
          <>
            {step === 1
              ? <Button variant="secondary" onClick={reset} icon="🔄">Cambiar archivo</Button>
              : <Button variant="ghost" onClick={() => step > 0 ? setStep(s => s - 1) : null} disabled={step === 0}>← Atrás</Button>
            }
            {step !== 1 && step < 3 && step !== 0 && <Button onClick={handleNext} disabled={!canNext()}>Siguiente →</Button>}
            {step === 1 && analysis && !analyzing && Object.keys(editedContent).length > 0 && <Button onClick={() => setStep(2)}>Continuar →</Button>}
            {step === 1 && analyzing && <Button disabled loading>Generando captions...</Button>}
            {step === 1 && analysis && !analyzing && Object.keys(editedContent).length === 0 && <Button disabled>Preparando...</Button>}
            {step === 3 && <Button onClick={() => setStep(4)} disabled={selectedNetworks.length === 0}>Revisar y publicar →</Button>}
          </>
        )}
      </div>
    </div>

    {/* ─── Modal YouTube Options ─────────────────────────── */}
    {showYoutubeModal && (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
      }} onClick={() => setShowYoutubeModal(false)}>
        <div style={{
          background: 'var(--bg-card)', borderRadius: 'var(--radius-xl)', padding: '28px',
          width: '100%', maxWidth: '460px', border: '1px solid #ef444440',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#FF000020', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>▶</div>
            <div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '17px', fontWeight: '800', margin: 0 }}>Configuración de YouTube</h3>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>Requerido por la política de YouTube</p>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '65vh', overflowY: 'auto', paddingRight: '4px' }}>
            {/* Título */}
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>📝 Título del video <span style={{color:'#ef4444'}}>*</span></label>
              <input
                value={youtubeOptions.title}
                onChange={e => setYoutubeOptions(p => ({...p, title: e.target.value}))}
                placeholder="Título del video (máx. 100 caracteres)"
                maxLength={100}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', boxSizing: 'border-box' }}
              />
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{youtubeOptions.title.length}/100</span>
            </div>

            {/* Descripción */}
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>📄 Descripción</label>
              <textarea
                value={youtubeOptions.description}
                onChange={e => setYoutubeOptions(p => ({...p, description: e.target.value}))}
                placeholder="Descripción del video (opcional, el caption se usa por defecto)"
                rows={3}
                maxLength={5000}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>

            {/* Miniatura */}
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>🖼 Miniatura personalizada</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{
                  padding: '8px 16px', borderRadius: 'var(--radius-md)', border: '1px dashed var(--border)',
                  background: 'var(--bg-elevated)', cursor: 'pointer', fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-body)',
                }}>
                  📁 Seleccionar imagen (JPG/PNG, máx. 2MB)
                  <input type="file" accept="image/jpeg,image/png" style={{ display: 'none' }} onChange={e => {
                    const f = e.target.files?.[0];
                    if (f && f.size <= 2097152) setYoutubeThumbnail(f);
                    else if (f) alert('La imagen debe ser menor a 2MB');
                  }} />
                </label>
                {youtubeThumbnail && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <img src={URL.createObjectURL(youtubeThumbnail)} alt="thumb" style={{ width: '48px', height: '27px', objectFit: 'cover', borderRadius: '4px', border: '1px solid var(--border)' }} />
                    <button onClick={() => setYoutubeThumbnail(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '14px' }}>✕</button>
                  </div>
                )}
              </div>
              <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>Requiere canal verificado en youtube.com/verify</p>
            </div>

            {/* Privacidad */}
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>🔒 Estado de privacidad</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[{val:'public',label:'🌍 Público'},{val:'unlisted',label:'🔗 No listado'},{val:'private',label:'🔐 Privado'}].map(opt => (
                  <button key={opt.val} onClick={() => setYoutubeOptions(p => ({...p, privacy_status: opt.val}))} style={{
                    flex: 1, padding: '8px 4px', borderRadius: '8px', border: `1px solid ${youtubeOptions.privacy_status === opt.val ? '#FF0000' : 'var(--border)'}`,
                    background: youtubeOptions.privacy_status === opt.val ? '#FF000015' : 'var(--bg-elevated)',
                    color: youtubeOptions.privacy_status === opt.val ? '#FF0000' : 'var(--text-muted)',
                    fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'var(--font-body)',
                  }}>{opt.label}</button>
                ))}
              </div>
            </div>

            {/* Categoría */}
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>📂 Categoría</label>
              <select value={youtubeOptions.category_id} onChange={e => setYoutubeOptions(p => ({...p, category_id: e.target.value}))} style={{
                width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)',
              }}>
                <option value="1">Cine y Animación</option>
                <option value="2">Automóviles</option>
                <option value="10">Música</option>
                <option value="15">Mascotas y Animales</option>
                <option value="17">Deportes</option>
                <option value="20">Videojuegos</option>
                <option value="22">Personas y Blogs</option>
                <option value="23">Comedia</option>
                <option value="24">Entretenimiento</option>
                <option value="25">Noticias y Política</option>
                <option value="26">Estilo y Moda</option>
                <option value="27">Educación</option>
                <option value="28">Ciencia y Tecnología</option>
                <option value="29">Organizaciones sin fines de lucro</option>
              </select>
            </div>

            {/* Toggles */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600' }}>👶 Contenido para niños</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Obligatorio por ley COPPA</div>
                </div>
                <Toggle checked={youtubeOptions.made_for_kids} onChange={v => setYoutubeOptions(p => ({...p, made_for_kids: v}))} />
              </div>
              <div style={{ height: '1px', background: 'var(--border)' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600' }}>🤖 Contiene IA generativa</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Requerido si usaste IA para crear el contenido</div>
                </div>
                <Toggle checked={youtubeOptions.contains_ai} onChange={v => setYoutubeOptions(p => ({...p, contains_ai: v}))} />
              </div>
            </div>

            <div style={{ padding: '10px 14px', background: '#f59e0b10', borderRadius: 'var(--radius-md)', border: '1px solid #f59e0b30' }}>
              <p style={{ fontSize: '11px', color: '#f59e0b', margin: 0, lineHeight: 1.6 }}>
                ⚠️ Declarar información falsa puede resultar en penalizaciones o eliminación del video por parte de YouTube.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <Button variant="secondary" style={{ flex: 1 }} onClick={() => setShowYoutubeModal(false)}>Cancelar</Button>
            <Button style={{ flex: 1, background: '#FF0000', borderColor: '#FF0000' }} onClick={handlePublish}>
              ▶ Confirmar y publicar
            </Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
